import { useState } from "react";
import { Mail, Phone, MapPin, Award, CheckCircle, Loader2, Send } from "lucide-react";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    platform: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");
  const [leadDetails, setLeadDetails] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validate required fields
    if (!formData.name || !formData.email || !formData.phone) {
      setError("Please fill in all required fields");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);

    // Save lead to database (backup)
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          platform: formData.platform || "Not specified",
          message: formData.message || "",
          source: "contact-form"
        })
      });
    } catch (err) {
      console.error("Error saving lead:", err);
    }

    // Store lead details for display
    const leadText = `NEW LEAD - BrandBorn

Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Platform: ${formData.platform || "Not specified"}
Message: ${formData.message || "No message"}`;

    setLeadDetails(leadText);
    setIsSuccess(true);
    setIsSubmitting(false);
    
    // Reset form
    setFormData({
      name: "",
      email: "",
      phone: "",
      platform: "",
      message: ""
    });
  };

  // Copy to clipboard function
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert("✅ Copied! Now open WhatsApp on your phone and paste.");
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      alert("✅ Copied! Now open WhatsApp on your phone and paste.");
    });
  };

  return (
    <section id="contact" className="py-16 md:py-24 px-4 bg-gradient-to-br from-[#FF2A42] via-[#E11D48] to-[#7C3AED] text-white" data-testid="contact-section">
      <div className="max-w-4xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-bold mb-6">
          <Award size={16} />
          <span>98% SUCCESS RATE • 1000+ BRANDS ONBOARDED</span>
          <CheckCircle size={16} />
        </div>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">Ready to Launch Your Brand?</h2>
        <p className="text-lg md:text-xl mb-10 opacity-95 leading-relaxed">
          Get in touch with our e-commerce onboarding experts today. Join 1000+ brands who achieved success with our 98% approval rate.
        </p>
        
        <div className="bg-white rounded-2xl p-6 md:p-10 text-left shadow-2xl">
          {!isSuccess ? (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-gray-700 font-semibold mb-2">Your Name *</label>
                  <input 
                    type="text" 
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-gray-900"
                    placeholder="Enter your name"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-gray-700 font-semibold mb-2">Email Address *</label>
                  <input 
                    type="email" 
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-gray-900"
                    placeholder="your@email.com"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-gray-700 font-semibold mb-2">Phone Number *</label>
                  <input 
                    type="tel" 
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-gray-900"
                    placeholder="+91 XXXXX XXXXX"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <label htmlFor="platform" className="block text-gray-700 font-semibold mb-2">Interested Platform</label>
                  <select 
                    id="platform"
                    name="platform"
                    value={formData.platform}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-gray-900"
                    data-testid="contact-platform-select"
                  >
                    <option value="">Select platform</option>
                    <option value="Myntra">Myntra</option>
                    <option value="Nykaa">Nykaa</option>
                    <option value="Ajio">Ajio</option>
                    <option value="Blinkit">Blinkit</option>
                    <option value="Swiggy Instamart">Swiggy Instamart</option>
                    <option value="Amazon">Amazon</option>
                    <option value="Flipkart">Flipkart</option>
                    <option value="Multiple Platforms">Multiple Platforms</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label htmlFor="message" className="block text-gray-700 font-semibold mb-2">Your Message</label>
                <textarea 
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-gray-900"
                  placeholder="Tell us about your brand and requirements..."
                  data-testid="contact-message-textarea"
                ></textarea>
              </div>

              {error && (
                <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}
              
              <button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full bg-red-600 text-white px-8 py-5 rounded-2xl hover:bg-red-700 transition-all duration-500 font-black text-base tracking-wider uppercase shadow-[0_4px_20px_rgba(220,38,38,0.3)] hover:shadow-[0_12px_40px_rgba(220,38,38,0.4)] hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2"
                data-testid="contact-submit-button"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Talk to Growth Expert
                  </>
                )}
              </button>
            </form>
          ) : (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Thank You! 🎉
              </h3>
              <p className="text-gray-600 mb-4">
                Your inquiry has been received. We'll contact you within 24 hours.
              </p>
              
              {/* Lead Details Box - For business owner to copy */}
              <div className="bg-gray-100 rounded-xl p-4 text-left mb-4 max-w-md mx-auto">
                <p className="text-xs text-gray-500 mb-2">Lead Details (for your reference):</p>
                <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
                  {leadDetails}
                </pre>
              </div>
              
              {/* Copy Button */}
              <button
                onClick={() => copyToClipboard(leadDetails)}
                className="bg-green-500 text-white px-6 py-3 rounded-full font-semibold hover:bg-green-600 transition flex items-center justify-center gap-2 mx-auto"
              >
                📋 Copy Lead Details
              </button>
              
              <p className="text-xs text-gray-500 mt-4">
                Need immediate help? Call: <a href="tel:+919464645491" className="text-red-500 font-bold">+91 94646 45491</a>
              </p>
              
              <button
                onClick={() => {
                  setIsSuccess(false);
                  setLeadDetails("");
                }}
                className="mt-4 text-red-500 hover:text-red-600 font-semibold text-sm"
              >
                Submit Another Inquiry
              </button>
            </div>
          )}
          
          <div className="mt-8 pt-8 border-t border-gray-200">
            <div className="grid sm:grid-cols-3 gap-4 text-center">
              <div className="flex flex-col items-center gap-2">
                <Mail className="text-red-500" size={24} />
                <a href="mailto:business@brandborn.in" className="text-gray-700 hover:text-red-500 transition text-sm">business@brandborn.in</a>
              </div>
              <div className="flex flex-col items-center gap-2">
                <Phone className="text-red-500" size={24} />
                <a href="tel:+919464645491" className="text-gray-700 hover:text-red-500 transition text-sm">+91 94646 45491</a>
              </div>
              <div className="flex flex-col items-center gap-2">
                <MapPin className="text-red-500" size={24} />
                <span className="text-gray-700 text-sm">India</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
